import { closest, createElement, detach, isNullOrUndefined as isNOU, removeClass } from '@syncfusion/ej2-base';
import * as EVENTS from '../../common/constant';
import { SelectionCommands } from '../plugin';
export class FormatPainterActions {
    constructor(parent, options) {
        this.INVALID_TAGS = ['A', 'AUDIO', 'IMG', 'VIDEO', 'IFRAME'];
        this.parent = parent;
        this.settings = options;
        this.addEventListener();
        this.setDeniedFormats();
    }
    addEventListener() {
        this.parent.observer.on(EVENTS.FORMAT_PAINTER_ACTIONS, this.actionHandler, this);
        this.parent.observer.on(EVENTS.MODEL_CHANGED_PLUGIN, this.onPropertyChanged, this);
        this.parent.observer.on(EVENTS.INTERNAL_DESTROY, this.destroy, this);
    }
    onPropertyChanged(prop) {
        if (prop && prop.module === 'formatPainter') {
            if (!isNOU(prop.newProp.formatPainterSettings.allowedFormats)) {
                this.settings.allowedFormats = prop.newProp.formatPainterSettings.allowedFormats;
            }
            if (!isNOU(prop.newProp.formatPainterSettings.deniedFormats)) {
                this.settings.deniedFormats = prop.newProp.formatPainterSettings.deniedFormats;
                this.setDeniedFormats();
            }
        }
    }
    removeEventListener() {
        this.parent.observer.off(EVENTS.FORMAT_PAINTER_ACTIONS, this.actionHandler);
        this.parent.observer.off(EVENTS.MODEL_CHANGED_PLUGIN, this.onPropertyChanged);
        this.parent.observer.off(EVENTS.INTERNAL_DESTROY, this.destroy);
    }
    /**
     * Destroys the format painter.
     *
     * @function destroy
     * @returns {void}
     * @hidden
     * @private
     */
    destroy() {
        this.removeEventListener();
        this.INVALID_TAGS = null;
        this.copyCollection = null;
        this.deniedFormatsCollection = null;
        this.newElem = null;
        this.newElemLastChild = null;
        this.settings = null;
        this.parent = null;
    }
    actionHandler(args) {
        if (!isNOU(args) && !isNOU(args.item) && !isNOU(args.item.formatPainterAction)) {
            switch (args.item.formatPainterAction) {
                case 'format-copy':
                    this.copyAction();
                    break;
                case 'format-paste':
                    this.pasteAction();
                    break;
                case 'escape':
                    this.escapeAction();
                    break;
            }
            this.callBack(args);
        }
    }
    callBack(event) {
        if (event.callBack) {
            event.callBack({
                requestType: 'FormatPainter',
                action: event.item.formatPainterAction,
                event: event.event,
                editorMode: 'HTML',
                range: this.parent.nodeSelection.getRange(this.parent.currentDocument),
                elements: this.parent.nodeSelection.getSelectedNodes(this.parent.currentDocument)
            });
        }
    }
    generateElement() {
        const copyCollection = this.copyCollection.slice(); // To clone without reversing the collcection array
        copyCollection.reverse();
        const length = copyCollection.length;
        const elemCollection = createElement('div', { className: 'e-format-paste-wrapper' });
        let lastAppendChild;
        for (let i = 0; i < length; i++) {
            const { attrs, className, styles, tagName } = copyCollection[i];
            const elem = createElement(tagName);
            if (className !== '') {
                elem.className = className;
            }
            for (let i = 0; i < attrs.length; i++) {
                const property = attrs[i].name;
                const value = attrs[i].value;
                elem.setAttribute(property, value);
            }
            for (let i = 0; i < styles.length; i++) {
                const property = styles[i].property;
                const value = styles[i].value;
                const priority = styles[i].priority;
                elem.style.setProperty(property, value, priority);
            }
            if (elemCollection.childElementCount === 0) {
                elemCollection.append(elem);
                lastAppendChild = elem;
            }
            else {
                lastAppendChild.append(elem);
                lastAppendChild = elem;
            }
        }
        const elemChild = this.removeDeniedFormats(elemCollection);
        let currentElem = elemChild;
        while (currentElem) {
            if (currentElem.firstChild === null) {
                lastAppendChild = currentElem;
                currentElem = undefined;
            }
            else {
                currentElem = currentElem.firstChild;
            }
        }
        this.newElem = elemChild;
        this.newElemLastChild = lastAppendChild;
    }
    pasteAction() {
        if (isNOU(this.copyCollection) || this.copyCollection.length === 0) {
            this.paintPlainTextFormat();
            return;
        }
        this.insertFormatNode(this.newElem, this.newElemLastChild);
    }
    removeDeniedFormats(parentElement) {
        const deniedPropArray = this.deniedFormatsCollection;
        if (!isNOU(deniedPropArray) && deniedPropArray.length > 0) {
            const length = deniedPropArray.length;
            for (let i = 0; i < length; i++) {
                const currentRule = deniedPropArray[i];
                const tag = currentRule.tag;
                if (!tag) {
                    continue;
                }
                const elementsList = parentElement.querySelectorAll(tag);
                for (let j = 0; j < elementsList.length; j++) {
                    const currentElement = elementsList[j];
                    const classes = currentRule.classes;
                    const styles = currentRule.styles;
                    const attributes = currentRule.attributes;
                    // Step 1: Check class condition
                    let classConditionMet = true;
                    if (classes.length > 0 && classes[j].trim() !== '') {
                        classConditionMet = true;
                        for (let k = 0; k < classes.length; k++) {
                            if (currentElement.classList.contains(classes[k].trim())) {
                                classConditionMet = true;
                            }
                            else {
                                classConditionMet = false;
                            }
                        }
                    }
                    // Step 2: Check if styles or attributes are targeted
                    const hasExplicitTargets = (styles.length > 0 && styles[j].trim() !== '') ||
                        (attributes.length > 0 && attributes[j].trim() !== '');
                    // Step 3: Apply denial logic
                    if (classConditionMet) {
                        // Case A: Remove styles
                        if (hasExplicitTargets) {
                            for (let k = 0; k < styles.length; k++) {
                                const styleName = styles[k].trim();
                                if (styleName) {
                                    currentElement.style.removeProperty(styleName);
                                }
                            }
                            for (let k = 0; k < attributes.length; k++) {
                                const attrName = attributes[k].trim();
                                if (attrName) {
                                    currentElement.removeAttribute(attrName);
                                }
                            }
                        }
                        // Case B: Remove classes
                        if (classes.length > 0) {
                            for (let k = 0; k < classes.length; k++) {
                                const className = classes[k].trim();
                                if (className) {
                                    removeClass([currentElement], className);
                                }
                            }
                        }
                        // Final cleanup
                        if (currentElement.style.length === 0) {
                            currentElement.removeAttribute('style');
                        }
                        if (currentElement.classList.length === 0) {
                            currentElement.removeAttribute('class');
                        }
                    }
                }
            }
        }
        return parentElement.firstElementChild;
    }
    copyAction() {
        const copyCollection = [];
        const range = this.parent.nodeSelection.getRange(this.parent.currentDocument);
        const domSelection = this.parent.nodeSelection;
        let nodes = range.collapsed ? domSelection.getSelectionNodeCollection(range) :
            domSelection.getSelectionNodeCollectionBr(range);
        if (nodes.length === 0 && domSelection.getSelectionNodeCollectionBr(range).length === 0) {
            return;
        }
        else {
            nodes = nodes.length === 0 ? domSelection.getSelectionNodeCollectionBr(range) : nodes;
        }
        let parentElem = nodes[0].parentElement;
        let currentContext = this.findCurrentContext(parentElem);
        const allowedRulesArray = this.settings.allowedFormats.indexOf(';') > -1 ? this.settings.allowedFormats.split(';') :
            [this.settings.allowedFormats];
        for (let i = 0; i < allowedRulesArray.length; i++) {
            allowedRulesArray[i] = allowedRulesArray[i].trim();
        }
        const [rangeParentElem, context] = this.getRangeParentElem(currentContext, parentElem);
        if (currentContext === null) {
            currentContext = context;
        }
        if (!isNOU(currentContext)) {
            if (range.startContainer.nodeName === '#text') {
                parentElem = range.startContainer.parentElement;
            }
            const lastElement = parentElem;
            do {
                if (allowedRulesArray.indexOf(parentElem.nodeName.toLowerCase()) > -1) {
                    const allAttributes = parentElem.attributes;
                    const attribute = [];
                    for (let i = 0; i < allAttributes.length; i++) {
                        if (allAttributes[i].name !== 'class' && allAttributes[i].name !== 'style') {
                            attribute.push(allAttributes[i]);
                        }
                    }
                    const classes = parentElem.className;
                    const allStyles = parentElem.style;
                    const styleProp = [];
                    for (let i = 0; i < allStyles.length; i++) {
                        const property = allStyles[i];
                        const value = allStyles.getPropertyValue(property);
                        const priority = allStyles.getPropertyPriority(property);
                        styleProp.push({ property: property, value: value, priority: priority });
                    }
                    copyCollection.push({
                        attrs: attribute, className: classes, styles: styleProp, tagName: parentElem.nodeName
                    });
                }
                if (rangeParentElem === parentElem) {
                    parentElem = undefined;
                }
                else if (!isNOU(parentElem.parentElement)) {
                    parentElem = parentElem.parentElement;
                }
                if (lastElement === parentElem) {
                    break;
                }
            } while (!isNOU(parentElem) || parentElem === this.parent.editableElement);
            this.copyCollection = copyCollection;
        }
        this.generateElement();
    }
    getRangeParentElem(currentContext, rangeParent) {
        let startContainer = rangeParent;
        let rangeParentELem;
        if (startContainer.nodeType === 3) {
            startContainer = startContainer.parentElement;
        }
        switch (currentContext) {
            case 'Table':
                rangeParentELem = closest(startContainer, 'td');
                if (isNOU(rangeParentELem)) {
                    rangeParentELem = closest(startContainer, 'th');
                }
                break;
            case 'List':
                rangeParentELem = closest(startContainer, 'li');
                break;
            case 'Text':
                rangeParentELem = closest(startContainer, 'p');
                break;
        }
        if (isNOU(rangeParentELem)) {
            const nearBlockParentName = this.getNearestBlockParentElement(rangeParent);
            if (!isNOU(nearBlockParentName) && nearBlockParentName !== 'UL' &&
                nearBlockParentName !== 'OL' && nearBlockParentName !== 'LI') {
                rangeParentELem = closest(startContainer, nearBlockParentName);
                currentContext = 'Text';
            }
        }
        if (currentContext === 'List') {
            rangeParentELem = rangeParentELem.parentElement;
        }
        return [rangeParentELem, currentContext];
    }
    getNearestBlockParentElement(rangeParent) {
        let node = rangeParent;
        if (node.nodeType === 3) {
            node = node.parentNode;
        }
        // iterate untill the content editable div
        while (node && node !== this.parent.editableElement) {
            // If true return the block node name.
            if (!isNOU(node) && this.isBlockElement(node)) {
                return node.nodeName;
            }
            // if false re assign node to parent node
            node = node.parentNode;
        }
        return null;
    }
    isBlockElement(node) {
        const blockTags = ['P', 'DIV', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
            'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'HEADER',
            'HR', 'MAIN', 'NAV', 'SECTION', 'SUMMARY', 'PRE'];
        return blockTags.indexOf(node.nodeName) > -1;
    }
    escapeAction() {
        this.copyCollection = [];
    }
    paintPlainTextFormat() {
        const range = this.parent.nodeSelection.getRange(this.parent.currentDocument);
        const domSelection = this.parent.nodeSelection;
        const nodes = range.collapsed ? domSelection.getSelectionNodeCollection(range) :
            domSelection.getSelectionNodeCollectionBr(range);
        let isInValid;
        if (nodes.length > 1) {
            for (let i = 0; i < nodes.length; i++) {
                isInValid = this.validateELementTag(nodes[i]);
            }
        }
        else {
            isInValid = this.validateELementTag(range.startContainer) && this.validateELementTag(range.endContainer);
        }
        if (!isInValid) {
            this.parent.execCommand('Clear', 'ClearFormat', null, null);
        }
    }
    validateELementTag(node) {
        if (node.nodeType === 3) {
            node = node.parentElement;
        }
        return this.INVALID_TAGS.indexOf(node.tagName) > -1;
    }
    findCurrentContext(parentElem) {
        const closestParagraph = closest(parentElem, 'p');
        const closestList = closest(parentElem, 'li');
        if (closestParagraph && !closestList) {
            return 'Text';
        }
        else if (closest(parentElem, 'li')) {
            if (!isNOU(closestParagraph) && !isNOU(closestList) && closestParagraph.textContent.trim() !== closestList.textContent.trim()) {
                return 'Text';
            }
            return 'List';
        }
        else if (closest(parentElem, 'td') || closest(parentElem, 'tr') || closest(parentElem, 'th')) {
            return 'Table';
        }
        return null;
    }
    insertFormatNode(elem, lastChild) {
        let clonedElem = elem.cloneNode(true);
        if (!this.isBlockElement(elem)) {
            const newBlockElem = createElement('P');
            newBlockElem.appendChild(elem);
            clonedElem = newBlockElem.cloneNode(true);
        }
        const endNode = this.parent.editableElement;
        const docElement = this.parent.currentDocument;
        let childElem = clonedElem.firstChild;
        let inlineElement;
        while (childElem) {
            if (this.isBlockElement(childElem)) {
                childElem = childElem.firstChild;
            }
            else {
                inlineElement = childElem.parentNode.removeChild(childElem);
                break;
            }
        }
        const formatValues = {
            element: inlineElement,
            lastChild: lastChild
        };
        SelectionCommands.applyFormat(docElement, null, endNode, 'P', null, 'formatPainter', null, formatValues);
        const range = this.parent.nodeSelection.getRange(docElement);
        const isCollapsed = range.collapsed;
        const blockNodes = this.parent.domNode.blockNodes();
        const isListCopied = this.isListCopied();
        if (isListCopied) {
            for (let i = 0; i < blockNodes.length; i++) {
                if (closest(blockNodes[i], 'li')) {
                    blockNodes[i] = closest(blockNodes[i], 'li');
                }
            }
        }
        let isFullNodeSelected = false;
        if (blockNodes.length === 1) {
            isFullNodeSelected = blockNodes[0].textContent.trim() === range.toString().trim();
        }
        if (this.isBlockElement(clonedElem) && isCollapsed || blockNodes.length > 1 || isFullNodeSelected) {
            this.insertBlockNode(clonedElem, range, docElement, blockNodes);
        }
    }
    isListCopied() {
        let isListCopied = false;
        for (let i = 0; i < this.copyCollection.length; i++) {
            if (this.copyCollection[i].tagName === 'OL' || this.copyCollection[i].tagName === 'UL') {
                isListCopied = true;
                break;
            }
        }
        return isListCopied;
    }
    insertBlockNode(element, range, docElement, nodes) {
        const domSelection = this.parent.nodeSelection;
        const saveSelection = domSelection.save(range, docElement);
        this.parent.domNode.setMarker(saveSelection);
        let listElement; // To clone to multiple list elements
        let cloneListParentNode;
        let sameListType = false;
        if (element.nodeName === 'UL' || element.nodeName === 'OL') {
            cloneListParentNode = element.cloneNode(true);
            listElement = cloneListParentNode.firstChild;
        }
        const cloneElementNode = isNOU(cloneListParentNode) ? element : element.firstChild;
        for (let index = 0; index < nodes.length; index++) {
            if (this.INVALID_TAGS.indexOf(nodes[index].nodeName) > -1 ||
                nodes[index].querySelectorAll('img,audio,video,iframe').length > 0) {
                continue;
            }
            const cloneParentNode = cloneElementNode.cloneNode(false);
            // Appending all the child elements
            while (nodes[index].firstChild) {
                if (nodes[index].textContent.trim().length !== 0) {
                    cloneParentNode.appendChild(nodes[index].firstChild);
                }
                else {
                    nodes[index].removeChild(nodes[index].firstChild);
                }
            }
            if (nodes[index].nodeName === 'TD' || nodes[index].nodeName === 'TH') {
                if (isNOU(cloneListParentNode)) {
                    nodes[index].appendChild(cloneParentNode);
                    continue;
                }
                else if (index === 0 && !isNOU(cloneListParentNode)) {
                    nodes[index].appendChild(cloneListParentNode);
                    cloneListParentNode.appendChild(cloneParentNode);
                    continue;
                }
                else {
                    nodes[index].appendChild(cloneParentNode);
                    continue;
                }
            }
            if (!isNOU(cloneListParentNode)) {
                sameListType = this.isSameListType(element, nodes[index]);
            }
            if (cloneParentNode.nodeName === 'LI' && !sameListType) {
                this.insertNewList(range, nodes, index, cloneListParentNode, cloneParentNode);
            }
            else if (sameListType) {
                this.insertSameList(nodes, index, cloneListParentNode, cloneParentNode);
            }
            else {
                nodes[index].parentNode.replaceChild(cloneParentNode, nodes[index]);
            }
            /**Removing the inserted block node in list and appending to previous element sibling */
            if (cloneParentNode.nodeName !== 'LI' && (cloneParentNode.parentElement.nodeName === 'OL' ||
                cloneParentNode.parentElement.nodeName === 'UL')) {
                const parent = cloneParentNode.parentElement;
                // Cutting single ul or ol to two ul or ol based on the range
                this.parent.nodeCutter.SplitNode(range, parent, true);
                if (!isNOU(parent.previousElementSibling)) {
                    parent.previousElementSibling.after(cloneParentNode);
                    // To remove the nested list items out of the block element
                    if (cloneParentNode.childNodes.length > 1) {
                        for (let j = 0; j < cloneParentNode.childNodes.length; j++) {
                            const currentChild = cloneParentNode.childNodes[j];
                            if (currentChild.nodeName === 'OL' || currentChild.nodeName === 'UL') {
                                cloneParentNode.after(currentChild);
                            }
                        }
                    }
                }
                else {
                    parent.parentElement.prepend(cloneParentNode);
                }
            }
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !isNOU(listElement) ? detach(listElement) : false;
        this.cleanEmptyLists();
        const save = this.parent.domNode.saveMarker(saveSelection);
        save.restore();
    }
    insertNewList(range, nodes, index, cloneListParentNode, cloneParentNode) {
        // Appending the li nodes to the ol or ul node
        if (index === 0) {
            const nodeName = nodes[index].nodeName;
            nodes[index] = nodes[index].parentNode.replaceChild(cloneListParentNode, nodes[index]);
            const parent = nodeName === 'LI' ? cloneListParentNode.parentElement
                : cloneListParentNode;
            // Splicing and then inserting the node to previous element sibling of the Listparent.parent
            this.parent.nodeCutter.SplitNode(range, parent, true);
            if (nodes[index].nodeName === 'LI' && !isNOU(parent)) {
                cloneListParentNode.append(cloneParentNode);
                if (!isNOU(parent.parentNode)) {
                    parent.parentNode.insertBefore(cloneListParentNode, parent);
                }
            }
            else {
                if (!isNOU(parent)) {
                    if (!isNOU(parent.previousElementSibling) && parent.previousElementSibling.nodeName === cloneListParentNode.nodeName) {
                        const currentParent = parent.previousElementSibling;
                        currentParent.append(cloneParentNode);
                        while (currentParent.firstChild) {
                            cloneListParentNode.append(currentParent.firstChild);
                        }
                    }
                    else if (!isNOU(parent.nextElementSibling) && parent.nextElementSibling.nodeName === cloneListParentNode.nodeName) {
                        const currentParent = parent.nextElementSibling;
                        currentParent.prepend(cloneParentNode);
                        while (currentParent.firstChild) {
                            cloneListParentNode.append(currentParent.firstChild);
                        }
                    }
                    else {
                        cloneListParentNode.append(cloneParentNode);
                    }
                }
                else {
                    cloneListParentNode.append(cloneParentNode);
                }
            }
        }
        else {
            cloneListParentNode.append(cloneParentNode);
        }
        this.detachEmptyBlockNodes(nodes[index]);
    }
    insertSameList(nodes, index, cloneListParentNode, cloneParentNode) {
        if (index === 0) {
            if (!isNOU(nodes[index].parentNode) && (nodes[index].parentNode.nodeName === 'UL' || nodes[index].parentNode.nodeName === 'OL')) {
                // append the nodes[index].parentNode.childNodes to the clonelistparentnode
                if (nodes.length === 1) {
                    // When clicked with cursor in the single list item
                    while (cloneParentNode.firstChild) {
                        nodes[index].append(cloneParentNode.firstChild);
                    }
                    for (let i = 0; i < nodes[index].parentNode.childNodes.length; i++) {
                        const currentChild = nodes[index].parentNode.childNodes[i];
                        cloneListParentNode.append(currentChild.cloneNode(true));
                    }
                }
                else {
                    cloneListParentNode.append(cloneParentNode);
                }
                let childNodesTextContent;
                if (nodes[index].parentNode) {
                    childNodesTextContent = Array.from(nodes[index].parentNode.childNodes).map((node) => node.textContent.trim()).join('');
                }
                const nodesTextContent = Array.from(nodes).map((node) => node.textContent.trim()).join('');
                if (childNodesTextContent === nodesTextContent || (nodes[index].parentNode &&
                    nodes[index].parentNode.textContent === cloneListParentNode.textContent)) {
                    // replace the older ol and ul with new ol and ul of clonelistparentnode
                    nodes[index].parentNode.parentNode.replaceChild(cloneListParentNode, nodes[index].parentNode);
                }
                else {
                    nodes[index].parentNode.replaceChild(cloneParentNode, nodes[index]);
                }
            }
        }
        else {
            if (cloneListParentNode.parentNode) {
                cloneListParentNode.append(cloneParentNode);
            }
            else {
                nodes[index].parentNode.replaceChild(cloneParentNode, nodes[index]);
            }
        }
        this.detachEmptyBlockNodes(nodes[index]);
    }
    isSameListType(element, node) {
        let isSameListType = false;
        const nearestListNode = closest(node, 'ol, ul');
        if (!isNOU(nearestListNode) && nearestListNode.querySelectorAll('li').length > 0) {
            if (nearestListNode.nodeName === element.nodeName) {
                isSameListType = true;
            }
            else {
                isSameListType = false;
            }
        }
        return isSameListType;
    }
    cleanEmptyLists() {
        const listElem = this.parent.editableElement.querySelectorAll('ol, ul');
        for (let i = 0; i < listElem.length; i++) {
            if (listElem[i].textContent.trim() === '') {
                detach(listElem[i]);
            }
        }
    }
    setDeniedFormats() {
        const deniedFormatsCollection = [];
        if (isNOU(this.settings) || isNOU(this.settings.deniedFormats)) {
            return;
        }
        const deniedFormats = this.settings.deniedFormats.indexOf(';') > -1 ? this.settings.deniedFormats.split(';') :
            [this.settings.deniedFormats];
        const length = deniedFormats.length;
        for (let i = 0; i < length; i++) {
            const formatString = deniedFormats[i];
            if (formatString !== '') {
                formatString.trim();
                const collection = this.makeDeniedFormatsCollection(formatString);
                if (!isNOU(collection)) {
                    deniedFormatsCollection.push(collection);
                }
            }
        }
        this.deniedFormatsCollection = deniedFormatsCollection;
    }
    detachEmptyBlockNodes(node) {
        if (!isNOU(node) && node.textContent.trim() === '') {
            detach(node);
        }
    }
    makeDeniedFormatsCollection(value) {
        const openParenIndex = value.indexOf('(');
        const closeParenIndex = value.indexOf(')');
        const openBracketIndex = value.indexOf('[');
        const closeBracketIndex = value.indexOf(']');
        const openBraceIndex = value.indexOf('{');
        const closeBraceIndex = value.indexOf('}');
        let classes = [];
        let attributes = '';
        let styles = '';
        let tagName = '';
        let classList = [];
        let attributesList = [];
        let stylesList = [];
        if (openParenIndex > -1 && closeParenIndex > -1) {
            classes = value.substring(openParenIndex + 1, closeParenIndex).split(' ');
            classList = classes[0].split(')')[0].split(',');
        }
        if (openBracketIndex > -1 && closeBracketIndex > -1) {
            attributes = value.substring(openBracketIndex + 1, closeBracketIndex);
            attributesList = attributes.split(',');
        }
        if (openBraceIndex > -1 && closeBraceIndex > -1) {
            styles = value.substring(openBraceIndex + 1, closeBraceIndex);
            stylesList = styles.split(',');
        }
        let openIndexArray = [openParenIndex, openBracketIndex, openBraceIndex];
        openIndexArray = openIndexArray.filter((index) => index > -1);
        const len = openIndexArray.length;
        let min;
        if (len === 1) {
            min = openIndexArray[0];
        }
        else if (len === 2) {
            min = Math.min(openIndexArray[0], openIndexArray[1]);
        }
        else if (len === 3) {
            min = Math.min(openIndexArray[0], openIndexArray[1], openIndexArray[2]);
        }
        tagName = value.substring(0, min);
        tagName = tagName.trim();
        return ({
            tag: tagName, styles: stylesList, classes: classList,
            attributes: attributesList
        });
    }
}
